import React, { FC, useCallback, useRef, useState } from 'react';
import { Box, Skeleton, SxProps, Theme, useMediaQuery, useTheme } from '@mui/material';
import { FileVariantsEnum, UserDto } from '@sweatpals/shared';
import { UserProfileMenu } from './user-profile-menu.component';
import { ArrowDownIcon, Logo, LogoWithLightText, SearchIcon2, TicketFilled, TicketOutlined } from '../../../icons';
import { Colors } from '../../../constants';
import { RainbowButton } from '../../buttons';
import { CoreButtonBase, CoreIconButton, CoreInput } from '../../_core';
import { UserAvatar } from '../../user-avatar';
import { clsx } from '../../../utils';
import {
	launchEventButtonStyles,
	systemHeaderStyles,
	ticketsButtonStyles,
	userMenuButtonStyles,
	searchInputStyles,
} from './system-header.styles';

export interface SystemHeaderProps {
	isPurchasesPage?: boolean;
	isLoading?: boolean;
	currentUser?: UserDto | null;
	sx?: SxProps<Theme>;

	getPublicFileUrl(fileId: string, variant?: FileVariantsEnum): string;
	openPurchasesPage?(): void;
	openCreateEventPage?(): void;
	openEventSearchPage?(search?: string): void;
	openDashboardPage?(): void;
	openLoginPage?(): void;
	openCreateCommunityPage?(): void;
	openDownloadAppPage?(): void;
	handleLogout?(): void;
	openBookDemoPage?(): void;
}

export const SystemHeader: FC<SystemHeaderProps> = (props) => {
	const {
		isPurchasesPage = false,
		isLoading = false,
		currentUser,
		sx,
		getPublicFileUrl,
		openPurchasesPage,
		openCreateEventPage,
		openLoginPage,
		openCreateCommunityPage,
		openEventSearchPage,
		openDashboardPage,
		openDownloadAppPage,
		handleLogout,
		openBookDemoPage,
	} = props;
	const [search, setSearch] = useState('');
	const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
	const ref = useRef<HTMLButtonElement | null>(null);
	const theme = useTheme();
	const isLessThanMd = useMediaQuery(theme.breakpoints.down('md'));

	const handleCloseUserMenu = useCallback(() => {
		setIsUserMenuOpen(false);
	}, []);

	const handleOpenUserMenu = useCallback(() => {
		setIsUserMenuOpen(true);
	}, []);

	return (
		<Box sx={clsx(systemHeaderStyles, sx)}>
			<Box flex={1} pl={{ xs: '16px', md: '32px' }}>
				<Box
					component={isLessThanMd ? Logo : LogoWithLightText}
					sx={{
						width: '129px',
						height: '21px',

						[theme.breakpoints.down('md')]: {
							width: '21px',
							color: Colors['F84B0B'],
						}
					}}
				/>
			</Box>

			<Box justifyContent="center" width="100%" maxWidth={{ xs: '280px', md: '350px' }}>
				<CoreInput
					value={search}
					startAdornment={<SearchIcon2 />}
					placeholder="Search events & communities..."
					sx={searchInputStyles}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							openEventSearchPage?.(search);
						}
					}}
					onChange={(e) => {
						setSearch(e.target.value);
					}}
				/>
			</Box>

			<Box flex={1} justifyContent="flex-end" pr="16px">
				<Box display="flex" alignItems="center" gap={{ xs: '12px', md: '24px' }}>
					<RainbowButton sx={launchEventButtonStyles} onClick={openCreateEventPage}>
						{isLessThanMd ? '+ Event' : '🚀 Launch an event'}
					</RainbowButton>
					<CoreIconButton sx={ticketsButtonStyles} disabled={isPurchasesPage} onClick={openPurchasesPage}>
						<Box
							component={isPurchasesPage ? TicketFilled : TicketOutlined}
							color={isPurchasesPage ? Colors.white : Colors['737373']}
							width={24}
							height={24}
						/>
					</CoreIconButton>
				</Box>

				<Box height="100%" width="1px" ml="20px" mr="16px" bgcolor={Colors['2A2A2A']} />


				{isLoading ? (
					<Skeleton
						sx={{
							height: '36px',
							width: '73px',
							borderRadius: '36px',
							backgroundColor: Colors.white_a15,
						}}
						variant="rectangular"
					/>
				) : currentUser ? (
					<CoreButtonBase ref={ref} variant="secondary" onClick={handleOpenUserMenu} sx={userMenuButtonStyles}>
						<UserAvatar
							fullName={currentUser.fullName}
							avatarSrc={currentUser.avatarId && getPublicFileUrl(currentUser.avatarId, FileVariantsEnum.XS)}
							emptyAvatarVariant={currentUser.emptyAvatarVariant}
							sx={{ width: '32px', height: '32px' }}
						/>
						<Box
							component={ArrowDownIcon}
							sx={{
								width: '28px',
								height: '28px',
								color: Colors.white,
								transform: isUserMenuOpen ? 'rotate(-180deg)' : undefined,
								transition: (theme) =>
									theme.transitions.create(['transform'], {
										duration: theme.transitions.duration.shortest,
									}),
							}}
						/>
					</CoreButtonBase>
				) : (
					<CoreButtonBase variant="secondary" onClick={openLoginPage}>
						Login
					</CoreButtonBase>
				)}
			</Box>

			<UserProfileMenu
				open={isUserMenuOpen}
				anchorEl={ref.current}
				onClose={handleCloseUserMenu}
				currentUser={currentUser}
				getPublicFileUrl={getPublicFileUrl}
				handleLogout={handleLogout}
				openDownloadAppPage={openDownloadAppPage}
				openDashboardPage={openDashboardPage}
				openCreateCommunityPage={openCreateCommunityPage}
				openBookDemoPage={openBookDemoPage}
			/>
		</Box>
	);
};
